import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useSessao } from './auth/SessaoContext';
import { rotaInicial } from './auth/rotas';
import { Girador, ProvedorSobreposicoes } from './componentes/ui';
import { AvisoCookies } from './componentes/AvisoCookies';
import cssMarca from './componentes/Marca.module.css';
import { Landing } from './paginas/Landing';
import { Privacidade } from './paginas/Privacidade';
import { Login } from './paginas/Login';
import { Cadastro } from './paginas/Cadastro';
import { VerificarEmail } from './paginas/VerificarEmail';
import { LayoutPainel } from './paginas/painel/Layout';
import { VisaoGeral } from './paginas/painel/VisaoGeral';
import { NovaBusca } from './paginas/painel/NovaBusca';
import { Downloads } from './paginas/painel/Downloads';
import { DetalheJob } from './paginas/painel/DetalheJob';
import { Configuracoes } from './paginas/painel/Configuracoes';
import { Creditos } from './paginas/painel/Creditos';
import { Suporte } from './paginas/painel/Suporte';
import { TrocarSenha } from './paginas/TrocarSenha';
import { LayoutAdmin } from './paginas/admin/LayoutAdmin';
import { VisaoGeralAdmin } from './paginas/admin/VisaoGeralAdmin';
import { UsuariosAdmin } from './paginas/admin/UsuariosAdmin';
import { VisitasAdmin } from './paginas/admin/VisitasAdmin';
import { FinanceiroDetalhe } from './paginas/admin/FinanceiroDetalhe';
import { PagamentosAdmin } from './paginas/admin/PagamentosAdmin';
import { ApiKeysAdmin } from './paginas/admin/ApiKeysAdmin';
import { AcessosAdmin } from './paginas/admin/AcessosAdmin';
import { AuditoriaAdmin } from './paginas/admin/AuditoriaAdmin';
import { SuporteAdmin } from './paginas/admin/SuporteAdmin';
import { CapturaAdmin } from './paginas/admin/CapturaAdmin';
import { ListaSped } from './paginas/painel/sped/ListaSped';
import { Empresas } from './paginas/painel/captura/Empresas';
import { NotasRecebidas } from './paginas/painel/captura/NotasRecebidas';
import { DetalheSped } from './paginas/painel/sped/DetalheSped';
import { PaginaGuia } from './paginas/Guia';
import { Desenvolvedores } from './paginas/Desenvolvedores';
import { GUIAS } from './conteudo/guias';

function Aguardando() {
  return (
    <div className={cssMarca.aguardando}>
      <Girador tamanho={26} />
    </div>
  );
}

/**
 * Exige sessão. Guarda a rota de origem para devolver o usuário a ela depois do
 * login — quem clicou num link de job não quer cair na visão geral.
 *
 * Isto é conveniência de navegação, não segurança: quem controla o navegador
 * consegue renderizar qualquer rota. O que protege os dados é a API recusar sem
 * cookie válido, e é lá que a verificação vale.
 */
function Protegida({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useSessao();
  const local = useLocation();

  if (carregando) return <Aguardando />;
  if (!usuario) return <Navigate to="/entrar" replace state={{ de: local }} />;
  // Senha definida por outra pessoa: a API já recusa tudo com 403, então
  // deixar navegar só produziria telas vazias cheias de erro.
  if (usuario.senhaProvisoria) return <Navigate to="/trocar-senha" replace />;
  return <>{children}</>;
}

/**
 * Restringe a rota a ADMIN.
 *
 * Isto é navegação, não segurança: quem controla o navegador renderiza a rota
 * que quiser. O que protege os dados é `@Roles(ADMIN)` na API, que responde 403
 * a qualquer requisição de conta comum.
 */
function SomenteAdmin({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useSessao();
  if (carregando) return <Aguardando />;
  if (usuario?.role !== 'ADMIN') return <Navigate to="/painel" replace />;
  return <>{children}</>;
}

/**
 * Exige sessão SEM exigir senha definitiva.
 *
 * Separada de {@link Protegida} porque a tela de troca de senha é o único lugar
 * que a sessão provisória pode alcançar — usar a outra aqui daria um laço de
 * redirecionamento.
 */
function SomenteAutenticado({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useSessao();
  const local = useLocation();

  if (carregando) return <Aguardando />;
  if (!usuario) return <Navigate to="/entrar" replace state={{ de: local }} />;
  return <>{children}</>;
}

/** Landing/login não fazem sentido para quem já está autenticado. */
function SomenteAnonimo({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useSessao();
  if (carregando) return <Aguardando />;
  if (usuario) return <Navigate to={rotaInicial(usuario)} replace />;
  return <>{children}</>;
}

export function App() {
  return (
    // Confirmações e notificações valem para qualquer tela.
    <ProvedorSobreposicoes>
      {/* Fora do <Routes>: o aviso acompanha qualquer tela, inclusive o
          painel — o consentimento é do navegador, não da página. */}
      <AvisoCookies />
      <Rotas />
    </ProvedorSobreposicoes>
  );
}

function Rotas() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacidade" element={<Privacidade />} />
      <Route path="/desenvolvedores" element={<Desenvolvedores />} />
      {GUIAS.map((g) => (
        <Route key={g.rota} path={g.rota} element={<PaginaGuia guia={g} />} />
      ))}
      <Route
        path="/entrar"
        element={
          <SomenteAnonimo>
            <Login />
          </SomenteAnonimo>
        }
      />
      <Route
        path="/criar-conta"
        element={
          <SomenteAnonimo>
            <Cadastro />
          </SomenteAnonimo>
        }
      />

      {/* Fora do painel: a sessão existe, mas não pode ir a lugar nenhum
          enquanto a senha for a que outra pessoa definiu. */}
      <Route
        path="/trocar-senha"
        element={
          <SomenteAutenticado>
            <TrocarSenha />
          </SomenteAutenticado>
        }
      />

      {/* Autenticada, mas fora do painel: a pessoa acabou de se cadastrar. */}
      <Route
        path="/verificar-email"
        element={
          <Protegida>
            <VerificarEmail />
          </Protegida>
        }
      />

      <Route
        path="/painel"
        element={
          <Protegida>
            <LayoutPainel />
          </Protegida>
        }
      >
        <Route index element={<VisaoGeral />} />
        <Route path="nova-busca" element={<NovaBusca />} />
        <Route path="downloads" element={<Downloads />} />
        <Route path="downloads/:jobId" element={<DetalheJob />} />
        <Route path="sped" element={<ListaSped />} />
        <Route path="sped/:id" element={<DetalheSped />} />
        <Route path="empresas" element={<Empresas />} />
        <Route path="notas-recebidas" element={<NotasRecebidas />} />
        <Route path="configuracoes" element={<Configuracoes />} />
        <Route path="creditos" element={<Creditos />} />
        <Route path="suporte" element={<Suporte />} />
      </Route>

      {/* Administração: árvore própria, com layout próprio. Aqui não existe
          nenhuma ação que baixe XML ou gaste crédito — é leitura e controle. */}
      <Route
        path="/admin"
        element={
          <Protegida>
            <SomenteAdmin>
              <LayoutAdmin />
            </SomenteAdmin>
          </Protegida>
        }
      >
        <Route index element={<VisaoGeralAdmin />} />
        <Route path="visitas" element={<VisitasAdmin />} />
        <Route path="clientes" element={<UsuariosAdmin />} />
        <Route path="pagamentos" element={<PagamentosAdmin />} />
        <Route path="financeiro" element={<FinanceiroDetalhe />} />
        <Route path="api-keys" element={<ApiKeysAdmin />} />
        <Route path="acessos" element={<AcessosAdmin />} />
        <Route path="auditoria" element={<AuditoriaAdmin />} />
        <Route path="suporte" element={<SuporteAdmin />} />
        <Route path="captura" element={<CapturaAdmin />} />
      </Route>

      {/* Rota desconhecida: manda para a home em vez de tela em branco. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
